import {Unit} from '@benchling/units';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import BenchlingQuery from 'graphql-client/BenchlingQuery';
import Tooltip from 'ui-library/bs/Tooltip';
import {BLUE_LIGHT, BLUE_POWDER_LIGHT, GRAY} from 'ui-library/Colors';
import SelectDropdown, {SelectDropdownErrorStyle} from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {getOptionDataPureTextName, OptionData} from 'ui-library/menus/MenuConstants';
import {getMatchingOptionDatas} from 'ui-library/menus/MenuUtil';
import Option from 'ui-library/menus/Option';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

import {UnitTypesQuery} from './__generated/UnitTypesQuery';
import {UNIT_TYPES_QUERY, UnitTypeFilter} from './UnitsConstants';
import {HydratedUnit, hydrateGqlUnit} from './util';

const NO_UNITS_MESSAGE =
  'No valid Units found. Your tenant admin can configure via Feature settings > Unit dictionary.';

const NoUnitsInfoMenu = (): JSX.Element => (
  <Menu>
    <Option>
      <div className={css(styles.infoOption)}>
        <BenchlingIcon style={{color: BLUE_LIGHT, marginRight: '8px'}} icon="info-circle" />
        {NO_UNITS_MESSAGE}
      </div>
    </Option>
  </Menu>
);

export const UnitOptionName = ({
  unitName,
  unitSymbol,
  unitTypeName,
}: {
  unitName: string;
  unitSymbol: string;
  unitTypeName?: string;
}): JSX.Element => (
  <span>
    <div>
      {unitName} ({unitSymbol})
    </div>
    {unitTypeName && <div className={css(styles.optionMetadata)}>{unitTypeName}</div>}
  </span>
);

interface GetUnitOptionsParams {
  unitTypeId?: string;
  baseUnitsOnly?: boolean;
  includeNone?: boolean;
  unitTypeFilter?: UnitTypeFilter;
}

export function getUnitOptions(
  allUnitTypes: UnitTypesQuery['unitTypes'],
  params: GetUnitOptionsParams
): Array<OptionData> {
  const {unitTypeId, baseUnitsOnly, includeNone, unitTypeFilter} = params;
  const unitTypes = allUnitTypes.filter(
    (ut) =>
      (!unitTypeId || ut.id === unitTypeId) &&
      (!unitTypeFilter || unitTypeFilter(ut)) &&
      !ut.archiveData.archived
  );
  // If we are explicitly filtering to a single unitType, hide the header.
  const hideHeader = unitTypes.length === 1 && (unitTypeId || unitTypeFilter);
  const options: Array<OptionData> = [];
  if (includeNone) {
    options.push({name: 'None', value: null});
  }
  for (const unitType of unitTypes) {
    if (baseUnitsOnly) {
      options.push({
        name: (
          <UnitOptionName
            unitName={unitType.baseUnit.name}
            unitSymbol={unitType.baseUnit.symbol}
            unitTypeName={unitType.name}
          />
        ),
        value: hydrateGqlUnit(unitType.baseUnit, unitType),
        pureTextName: `${unitType.name} ${unitType.baseUnit.name} ${unitType.baseUnit.symbol}`,
      });
    } else {
      if (!hideHeader) {
        options.push({name: unitType.name, isHeader: true});
      }
      const nonArchivedUnits = unitType.units.filter((u) => !u.archiveData.archived);
      for (const unit of nonArchivedUnits) {
        options.push({
          name: <UnitOptionName unitName={unit.name} unitSymbol={unit.symbol} />,
          value: hydrateGqlUnit(unit, unitType),
          pureTextName: `${unit.name} ${unit.symbol}`,
        });
      }
    }
  }
  return options;
}

function getDisplayName(
  selected: Unit | null | undefined,
  baseUnitsOnly?: boolean,
  useSymbol?: boolean,
  customPlaceholder?: string
): string {
  if (!selected) {
    return customPlaceholder ?? 'Select...';
  }
  return baseUnitsOnly ? selected.unitType.name! : useSymbol ? selected.symbol : selected.name;
}

export function getSortedMatches(query: string, options: Array<OptionData>): Array<OptionData> {
  const matches = getMatchingOptionDatas(query, options);
  return query
    ? matches.sort((a, b) => {
        const [aName, bName] = [getOptionDataPureTextName(a), getOptionDataPureTextName(b)];
        // Matches that start with the query string always come first; otherwise sort by ascending length
        return (
          Number(bName.startsWith(query)) - Number(aName.startsWith(query)) || aName.length - bName.length
        );
      })
    : matches;
}

export interface SearchableUnitDropdownProps {
  onSelect: (selected: HydratedUnit | null) => void;
  baseUnitsOnly?: boolean;
  buttonClassName?: string;
  className?: string;
  customPlaceholder?: string;
  disabled?: boolean;
  displaySelectionAsSymbol?: boolean;
  error?: string;
  errorStyle?: SelectDropdownErrorStyle;
  includeNone?: boolean;
  getOptionsFn?: (unitTypes: UnitTypesQuery['unitTypes']) => Array<OptionData>;
  unitTypeId?: string;
  unitTypeFilter?: UnitTypeFilter;
  selectedId?: string | null;
  icon?: BenchlingIconName;
}

interface QueriedProps {
  unitTypes: UnitTypesQuery['unitTypes'];
}

export function PresentationalSearchableUnitDropdown(
  props: SearchableUnitDropdownProps & QueriedProps
): JSX.Element {
  const {unitTypes, unitTypeId, baseUnitsOnly, includeNone, unitTypeFilter} = props;

  const options = props.getOptionsFn
    ? props.getOptionsFn(unitTypes)
    : getUnitOptions(unitTypes, {
        unitTypeId,
        baseUnitsOnly,
        includeNone,
        unitTypeFilter,
      });
  const selected = options.find((o) => o.value && o.value.id === props.selectedId)?.value as
    | HydratedUnit
    | undefined;

  return (
    <Tooltip disabled={!selected} title={getDisplayName(selected, props.baseUnitsOnly)}>
      <SelectDropdown
        className={props.className}
        buttonClassName={props.buttonClassName}
        displayName={getDisplayName(
          selected,
          props.baseUnitsOnly,
          props.displaySelectionAsSymbol,
          props.customPlaceholder
        )}
        disabled={props.disabled}
        error={props.error}
        errorStyle={props.errorStyle}
        icon={props.icon}
        shouldEllipsize={true}
        menu={
          options.length === 0 || (options.length === 1 && props.includeNone) ? (
            <NoUnitsInfoMenu />
          ) : (
            <SearchableMenu
              selectedValue={selected}
              onSelect={(selected: HydratedUnit | null): void => {
                props.onSelect(selected);
              }}
              optionDatas={options}
              getMatchingOptionDatas={getSortedMatches}
            />
          )
        }
        dataTestAttrs={{component: 'SearchableUnitDropdown', element: 'dropdown'}}
      />
    </Tooltip>
  );
}

export default function SearchableUnitDropdown(props: SearchableUnitDropdownProps): JSX.Element {
  return (
    <BenchlingQuery<UnitTypesQuery>
      query={UNIT_TYPES_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {({unitTypes}, {refetch}) => <PresentationalSearchableUnitDropdown {...props} unitTypes={unitTypes} />}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  optionMetadata: {color: GRAY},
  infoOption: {
    padding: '6px',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: BLUE_POWDER_LIGHT,
    margin: '-8px -16px -8px -16px',
    borderRadius: '4px',
  },
});
